import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send, Users, CheckCircle, AlertCircle } from 'lucide-react';

const EmailBlastPage = () => {
  const navigate = useNavigate();
  const [schools, setSchools] = useState([]);
  const [formData, setFormData] = useState({
    schoolId: 'all',
    role: 'all',
    subject: '',
    message: '',
    htmlMessage: '',
    includeUnverified: false
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [previewCount, setPreviewCount] = useState(0);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Load schools for the dropdown
  useEffect(() => {
    const fetchSchools = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/admin/schools`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );

        if (response.ok) {
          const data = await response.json();
          setSchools(data.schools || []);
        }
      } catch (error) {
        console.error('Error fetching schools:', error);
      }
    };

    fetchSchools();
  }, []);

  // Preview recipient count when filters change
  useEffect(() => {
    const getPreviewCount = async () => {
      if (!formData.schoolId && !formData.role) return;

      setLoadingPreview(true);
      try {
        const params = new URLSearchParams({
          schoolId: formData.schoolId,
          role: formData.role,
          includeUnverified: formData.includeUnverified.toString()
        });

        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/admin/email-blast/preview?${params}`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );

        if (response.ok) {
          const data = await response.json();
          setPreviewCount(data.count || 0);
        }
      } catch (error) {
        console.error('Error getting preview count:', error);
      } finally {
        setLoadingPreview(false);
      }
    };

    const debounceTimer = setTimeout(getPreviewCount, 500);
    return () => clearTimeout(debounceTimer);
  }, [formData.schoolId, formData.role, formData.includeUnverified]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.subject.trim() || !formData.message.trim()) {
      alert('Subject and message are required');
      return;
    }

    if (previewCount === 0) {
      alert('No users match the selected criteria');
      return;
    }

    const confirmMessage = `Are you sure you want to send this email to ${previewCount} users?`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/email-blast`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify(formData)
        }
      );

      const data = await response.json();

      if (response.ok) {
        setResult({
          success: true,
          ...data
        });
        // Reset form
        setFormData({
          schoolId: 'all',
          role: 'all',
          subject: '',
          message: '',
          htmlMessage: '',
          includeUnverified: false
        });
      } else {
        setResult({
          success: false,
          error: data.error || 'Failed to send email blast'
        });
      }
    } catch (error) {
      setResult({
        success: false,
        error: 'Network error: ' + error.message
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <button
          onClick={() => navigate('/admin')}
          className="text-red-600 hover:text-red-700 mb-4"
        >
          ← Back to Dashboard
        </button>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Email Blast</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          Send announcements to users filtered by school and role
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Form */}
        <div className="lg:col-span-2">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Filters */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    School Filter
                  </label>
                  <select
                    name="schoolId"
                    value={formData.schoolId}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="all">All Schools</option>
                    {schools.map(school => (
                      <option key={school.id} value={school.id}>
                        {school.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Role Filter
                  </label>
                  <select
                    name="role"
                    value={formData.role}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="all">All Roles</option>
                    <option value="student">Students</option>
                    <option value="moderator">Moderators</option>
                    <option value="admin">Administrators</option>
                  </select>
                </div>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="includeUnverified"
                  name="includeUnverified"
                  checked={formData.includeUnverified}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                />
                <label htmlFor="includeUnverified" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                  Include unverified users
                </label>
              </div>

              {/* Subject */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Subject *
                </label>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 dark:bg-gray-700 dark:text-white"
                  placeholder="Enter email subject..."
                />
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Message (Plain Text) *
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows={6}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 dark:bg-gray-700 dark:text-white"
                  placeholder="Enter your message..."
                />
              </div>

              {/* HTML Message (Optional) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  HTML Message (Optional)
                </label>
                <textarea
                  name="htmlMessage"
                  value={formData.htmlMessage}
                  onChange={handleInputChange}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 dark:bg-gray-700 dark:text-white"
                  placeholder="Enter HTML version of your message (optional)..."
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  If not provided, the plain text message will be converted to HTML
                </p>
              </div>

              {/* Submit Button */}
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={loading || previewCount === 0}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send size={16} className="mr-2" />
                      Send Email Blast
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Preview & Results */}
        <div className="space-y-6">
          {/* Recipient Preview */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
              <Users size={20} className="mr-2" />
              Recipients Preview
            </h3>
            
            <div className="text-center">
              {loadingPreview ? (
                <div className="flex justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-600"></div>
                </div>
              ) : (
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <div className="text-3xl font-bold text-gray-900 dark:text-white">
                    {previewCount.toLocaleString()}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    users will receive this email
                  </div>
                </div>
              )}
            </div>

            <div className="mt-4 text-xs text-gray-500 dark:text-gray-400">
              <div className="space-y-1">
                <div>School: {formData.schoolId === 'all' ? 'All Schools' : schools.find(s => s.id === formData.schoolId)?.name || 'Selected School'}</div>
                <div>Role: {formData.role === 'all' ? 'All Roles' : formData.role}</div>
                <div>Include Unverified: {formData.includeUnverified ? 'Yes' : 'No'}</div>
              </div>
            </div>
          </div>

          {/* Results */}
          {result && (
            <div className={`rounded-lg shadow p-6 ${result.success ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'}`}>
              <h3 className={`text-lg font-semibold mb-4 flex items-center ${result.success ? 'text-green-800 dark:text-green-400' : 'text-red-800 dark:text-red-400'}`}>
                {result.success ? (
                  <>
                    <CheckCircle size={20} className="mr-2" />
                    Email Blast Completed
                  </>
                ) : (
                  <>
                    <AlertCircle size={20} className="mr-2" />
                    Email Blast Failed
                  </>
                )}
              </h3>

              {result.success ? (
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Total Recipients:</span>
                    <span className="font-medium text-gray-900 dark:text-white">{result.totalRecipients}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Successfully Sent:</span>
                    <span className="font-medium text-green-600 dark:text-green-400">{result.successCount}</span>
                  </div>
                  {result.failureCount > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Failed:</span>
                      <span className="font-medium text-red-600 dark:text-red-400">{result.failureCount}</span>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-red-700 dark:text-red-300 text-sm">
                  {result.error}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailBlastPage;